import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import React, { FC, useEffect, useMemo } from 'react'

import { IconInfo } from '@avantstay/backoffice-vectors'

import { getNewUuid, useIsMobile } from '../../utils'
import { DragAndDropDirection, DragAndDropGrid } from '../DragAndDropGrid'
import { FileDropArea } from './components/FileDropArea'
import { FilePreview } from './components/FilePreview'
import { useManageAssets } from './hooks/useManageAssets'
import * as S from './UploadArea.styles'
import {
  AssetWithDocumentProps,
  FilePreviewType,
  FileViewMode,
  UploadAreaProps,
  UploadAreaType,
} from './UploadArea.types'
import { getFileNamesWithError, getPreviewTitleByType } from './UploadArea.utils'

const ITEMS_PER_LINE_GRID = 4
const ITEMS_PER_LINE_GRID_MOBILE = 2
const ITEMS_PER_LINE_LIST = 1

export const UploadArea: FC<UploadAreaProps> = ({
  accept,
  areaStyles,
  dropAreaMessage,
  filePreviewActions,
  fieldRestriction,
  hidePreview = false,
  initialFiles = [],
  filePreviewType = FilePreviewType.photoGallery,
  isSelecting = false,
  onChange,
  onChangeWithStatus,
  onSelectFile,
  onRemoveFile,
  onToggleEnableAsset,
  selectedFiles,
  enabledAssets,
  targetId,
  title,
  uploadAreaType,
  uploadSingleFile = false,
  uploadService,
  viewMode,
  disabled,
  hideUploadArea,
  forceResetFiles,
  previewTitleActions,
  photoGalleryFilters,
  filteredImages,
  disableDragAndDrop,
}) => {
  const {
    files,
    handleDrop,
    handleInputChange,
    handleRetryFile,
    sortFiles,
    removeFile,
    filesUploadProgress,
    filesWithError,
  } = useManageAssets(targetId, initialFiles, uploadService, forceResetFiles)

  const areaId = useMemo(() => getNewUuid(), [])

  const isMobile = useIsMobile()
  const isDocument = uploadAreaType === UploadAreaType.document
  const isReferencePhotoView = filePreviewType === FilePreviewType.referencePhoto

  const handleFileRemoval = (index: number, file: AssetWithDocumentProps) => {
    removeFile(index)
    onRemoveFile?.(file)
  }

  useEffect(() => {
    if (!isEqual(files, initialFiles)) {
      if (onChange) {
        onChange(files)
      }
    }

    if (!isEqual(files, initialFiles) || Object.keys(filesUploadProgress).length > 0) {
      if (onChangeWithStatus) {
        onChangeWithStatus({ files, filesUploadProgress, filesWithError })
      }
    }

    if (uploadSingleFile) {
      files.map((file, index) => index > 0 && handleFileRemoval?.(index, file))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, filesUploadProgress, filesWithError])

  const filesToShow =
    filteredImages !== undefined ? files.filter(file => filteredImages.includes(file.id)) : files

  const renderFilePreviewWithDraggableGrid = () => {
    const itemsPerLineOnGrid = isMobile ? ITEMS_PER_LINE_GRID_MOBILE : ITEMS_PER_LINE_GRID

    return (
      <S.PreviewContainer filePreviewType={filePreviewType}>
        {!isReferencePhotoView ? (
          <S.PreviewTitles>
            {getPreviewTitleByType(filePreviewType, files)} {previewTitleActions}
          </S.PreviewTitles>
        ) : null}
        <DragAndDropGrid
          items={filesToShow}
          itemsPerLine={
            viewMode && viewMode === FileViewMode.list ? ITEMS_PER_LINE_LIST : itemsPerLineOnGrid
          }
          direction={
            viewMode && viewMode === FileViewMode.list
              ? DragAndDropDirection.vertical
              : DragAndDropDirection.horizontal
          }
          onDragEnd={!disableDragAndDrop ? sortFiles : () => undefined}
          renderItem={(file, index) => {
            const handleMakeAsCover = () => {
              sortFiles(index, 0)
            }

            return (
              <FilePreview
                index={index}
                key={file.id}
                file={file}
                allFiles={files}
                filePreviewActions={filePreviewActions}
                fileUploadProgress={
                  filesUploadProgress[file.id as keyof typeof filesUploadProgress]
                }
                enableImageSize={viewMode === FileViewMode.list}
                filesWithError={filesWithError}
                isFileSelected={selectedFiles?.includes(file.id)}
                isFileEnabled={enabledAssets?.includes(file.id)}
                filePreviewType={filePreviewType}
                isSelecting={isSelecting}
                onMakeAsCover={handleMakeAsCover}
                onRetryUpload={handleRetryFile}
                onSelectFile={onSelectFile}
                onToggleEnableAsset={onToggleEnableAsset}
                removeFile={onRemoveFile ? () => handleFileRemoval(index, file) : undefined}
                fileViewMode={viewMode}
                fileCustomInfo={
                  initialFiles.find(initialFile => initialFile.id === file.id)?.customInfo
                }
              />
            )
          }}
        />
      </S.PreviewContainer>
    )
  }

  const fileNamesWithError = getFileNamesWithError({ files, filesWithError })

  return (
    <S.Container disabled={disabled} $hasFlexDirection={isReferencePhotoView}>
      {!hideUploadArea ? (
        <FileDropArea
          areaId={areaId}
          accept={accept}
          title={title}
          fieldRestriction={fieldRestriction}
          filePreviewType={filePreviewType}
          uploadAreaType={uploadAreaType}
          dropAreaMessage={dropAreaMessage}
          areaStyles={areaStyles}
          isDisabled={disabled}
          isDropAreaDisabled={disabled}
          shouldRenderDropArea={uploadSingleFile ? isEmpty(files) : true}
          uploadSingleFile={uploadSingleFile}
          onInputChange={handleInputChange}
          onDropFile={handleDrop}
        />
      ) : null}

      {fileNamesWithError.length > 0 ? (
        <S.ErrorContainer>
          <IconInfo />
          <div>
            <span>The following images have not been uploaded. Try to upload again:</span>
            <ul>
              {fileNamesWithError.map(file => (
                <li key={file}>
                  <S.ErrorIndexes>{file}</S.ErrorIndexes>
                </li>
              ))}
            </ul>
          </div>
        </S.ErrorContainer>
      ) : null}

      {photoGalleryFilters}

      {!hidePreview &&
        isDocument &&
        !isEmpty(files) &&
        filesToShow?.map(
          (file, index) =>
            filesUploadProgress !== undefined && (
              <FilePreview
                index={index}
                key={`${file.id}-${index}`}
                allFiles={files}
                file={file}
                fileUploadProgress={
                  filesUploadProgress[file.id as keyof typeof filesUploadProgress]
                }
                filesWithError={filesWithError}
                filePreviewType={filePreviewType}
                removeFile={handleFileRemoval ? () => handleFileRemoval(index, file) : undefined}
                uploadAreaType={uploadAreaType}
                fileViewMode={FileViewMode.grid}
              />
            ),
        )}

      {!hidePreview && !isDocument && renderFilePreviewWithDraggableGrid()}
    </S.Container>
  )
}
